import { useCallback, useEffect, useRef, useState } from 'react';
import { approveRequest as approveRequestApi, rejectRequest as rejectRequestApi } from '../api';

/**
 * Request approve/reject for the CST admin drawer.
 *
 * The salesperson-facing endpoints in way2news_ads_order_booking_api write
 * creative-type-change and design-change requests as plain rows into the
 * shared `booking_slot_creative_edit_logs` table — the same feed
 * `useAdminCreativeThread` already fetches in full for the Chat tab. This
 * hook used to fetch that `/logs` endpoint a second time into its own,
 * independent cache — which meant a message posted (or a live socket
 * update) in Chat never reached this tab, and vice versa for approve/reject.
 * It now reads the SAME `logs` array `useAdminCreativeThread` owns (passed
 * in by DetailDrawer as `logs`) and folds its own approve/reject results
 * back into that one cache via `onNewLog`, so there is exactly one source
 * of truth for both tabs.
 */
const useCreativeRequests = (bscId, logs, onNewLog) => {
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Approve one pending request log. Returns `true` on success (the new
   * `request_approved` log is folded into the shared thread cache so the
   * button disappears without a refetch), or an error message string on
   * failure.
   */
  const approve = useCallback(
    async (requestLogId) => {
      if (!bscId || approvingId != null) return false;
      setApprovingId(requestLogId);
      try {
        const data = await approveRequestApi(bscId, requestLogId);
        if (!mountedRef.current) return false;
        const newLog = Array.isArray(data?.logs) ? data.logs[0] : null;
        if (newLog) onNewLog(newLog);
        return true;
      } catch (err) {
        const message = err?.message || 'Unable to approve this request.';
        if (mountedRef.current) setError(message);
        return message;
      } finally {
        if (mountedRef.current) setApprovingId(null);
      }
    },
    [bscId, approvingId, onNewLog]
  );

  /**
   * Reject one pending request log. Returns `true` on success (the new
   * `request_rejected` log is folded into the shared thread cache so the
   * button disappears without a refetch), or an error message string on
   * failure.
   */
  const reject = useCallback(
    async (requestLogId) => {
      if (!bscId || rejectingId != null) return false;
      setRejectingId(requestLogId);
      try {
        const data = await rejectRequestApi(bscId, requestLogId);
        if (!mountedRef.current) return false;
        const newLog = Array.isArray(data?.logs) ? data.logs[0] : null;
        if (newLog) onNewLog(newLog);
        return true;
      } catch (err) {
        const message = err?.message || 'Unable to reject this request.';
        if (mountedRef.current) setError(message);
        return message;
      } finally {
        if (mountedRef.current) setRejectingId(null);
      }
    },
    [bscId, rejectingId, onNewLog]
  );

  return { logs, error, approve, approvingId, reject, rejectingId };
};

export default useCreativeRequests;
